import { TranslateService } from '@ngx-translate/core';
import { ChartDataset } from '@components/svg-chart';

/**
 * Configuration for temperature deviation band
 */
export interface TemperatureDeviationConfig {
  /** Deviation value in degrees (default: 3) */
  deviation: number;
  /** Color for the norm line */
  normColor: string;
  /** Opacity for the deviation band area (default: 0.2) */
  areaOpacity: number;
}

/**
 * Default configuration for temperature deviation
 */
export const DEFAULT_TEMPERATURE_DEVIATION_CONFIG: TemperatureDeviationConfig = {
  deviation: 3,
  normColor: '#ff9999', // Lighter red for temperature norm
  areaOpacity: 0.2
};

/**
 * Processed norm data with deviation bands
 */
export interface ProcessedNormData {
  /** Norm values */
  data: (number | null)[];
  /** Min values (norm - deviation) */
  dataMin: (number | null)[];
  /** Max values (norm + deviation) */
  dataMax: (number | null)[];
}

/**
 * Manager for weather norm data with deviation bands
 * Used to add ±3°C deviation bands for temperature norms
 */
export class WeatherNormDataManager {
  private config: TemperatureDeviationConfig;

  constructor(
    private translateService: TranslateService,
    config?: Partial<TemperatureDeviationConfig>
  ) {
    this.config = { ...DEFAULT_TEMPERATURE_DEVIATION_CONFIG, ...config };
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<TemperatureDeviationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get lighter color for norm line
   */
  getLighterColor(hexColor: string, factor: number = 0.4): string {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    const lightR = Math.round(r + (255 - r) * factor);
    const lightG = Math.round(g + (255 - g) * factor);
    const lightB = Math.round(b + (255 - b) * factor);

    return `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`;
  }

  /**
   * Process norm data and calculate deviation bands
   * @param normValues Array of norm values
   * @param deviation Deviation value (default from config)
   */
  processNormDataWithDeviation(
    normValues: (number | null)[],
    deviation?: number
  ): ProcessedNormData {
    const dev = deviation ?? this.config.deviation;

    const dataMin: (number | null)[] = normValues.map(value =>
      value !== null ? value - dev : null
    );

    const dataMax: (number | null)[] = normValues.map(value =>
      value !== null ? value + dev : null
    );

    return {
      data: normValues,
      dataMin,
      dataMax
    };
  }

  /**
   * Create a temperature norm dataset with deviation band
   * @param normValues Norm temperature values
   * @param indicatorLabel Label for the indicator (e.g., "Temperature")
   * @param baseColor Base color for the indicator
   * @param deviation Deviation value (default: 3)
   */
  createTemperatureNormDataset(
    normValues: (number | null)[],
    indicatorLabel: string,
    baseColor: string,
    deviation?: number
  ): ChartDataset {
    const { data, dataMin, dataMax } = this.processNormDataWithDeviation(normValues, deviation);
    const normColor = this.getLighterColor(baseColor);

    return {
      label: indicatorLabel + `(${this.translateService.instant('Norm')})`,
      data: data,
      type: 'line',
      borderColor: normColor,
      backgroundColor: normColor,
      borderWidth: 2,
      borderDash: [5, 5],
      showArea: true,
      dataMin: dataMin,
      dataMax: dataMax,
      areaOpacity: this.config.areaOpacity
    };
  }

  /**
   * Create a standard norm dataset without deviation band
   * Used for non-temperature indicators
   * @param normValues Norm values
   * @param indicatorLabel Label for the indicator
   * @param baseColor Base color for the indicator
   */
  createStandardNormDataset(
    normValues: (number | null)[],
    indicatorLabel: string,
    baseColor: string
  ): ChartDataset {
    const normColor = this.getLighterColor(baseColor);

    return {
      label: indicatorLabel + `(${this.translateService.instant('Norm')})`,
      data: normValues,
      type: 'line',
      borderColor: normColor,
      backgroundColor: normColor,
      borderWidth: 2,
      borderDash: [5, 5]
    };
  }

  /**
   * Determine if an indicator should have deviation band
   * Currently only temperature has deviation band
   */
  shouldHaveDeviationBand(indicator: string): boolean {
    const ind = (indicator || '').toLowerCase();
    return ind.includes('temp') || ind.includes('temperature');
  }

  /**
   * Create appropriate norm dataset based on indicator type
   * Automatically determines if deviation band should be shown
   * @param normValues Norm values
   * @param indicator Indicator type (e.g., 'temperature', 'precipitation')
   * @param indicatorLabel Translated label for the indicator
   * @param baseColor Base color for the indicator
   */
  createNormDataset(
    normValues: (number | null)[],
    indicator: string,
    indicatorLabel: string,
    baseColor: string
  ): ChartDataset {
    if (this.shouldHaveDeviationBand(indicator)) {
      return this.createTemperatureNormDataset(normValues, indicatorLabel, baseColor);
    }
    return this.createStandardNormDataset(normValues, indicatorLabel, baseColor);
  }
}
